import { InlineAlertType, Query, Typenames } from '@varo/graphql-schema-types';
import { openURL } from 'expo-linking';
import { NativeStackHeaderProps, Stack } from 'expo-router';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshControl, ScrollView, StyleSheet, View } from 'react-native';
import GetCallUsHoursOptions from 'services/graphql/queries/GetCallUsHoursOptions';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import InlineAlert from 'shared/components/vxl/InlineAlert/InlineAlert';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import TitleLgText from 'shared/components/vxl/typography/primary/title/TitleLgText';
import TitleMdText from 'shared/components/vxl/typography/primary/title/TitleMdText';
import { isWeb } from 'shared/constants/platform';
import { Support } from 'shared/constants/support';
import { useDefaultErrorStrings } from 'shared/hooks/errorFormatters/useDefaultError/useDefaultErrorStrings';
import DefaultPageDownErrorMessage from 'shared/patterns/vxl/errors/PageDownErrorMessage/DefaultPageDownErrorMessage';
import ScreenTransitionOverlay from 'shared/patterns/vxl/ScreenTransitionOverlay/ScreenTransitionOverlay';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';
import { useQuery } from 'urql';

const CallUs = () => {
  const { t: translatedText } = useTranslation('myVaro', {
    keyPrefix: 'help',
  });

  const { defaultTitle, defaultMessage } = useDefaultErrorStrings();

  const [{ data, fetching, error }, refetch] = useQuery<Query>({
    query: GetCallUsHoursOptions,
  });

  const callUsPageInfo =
    data?.callUsPage?.__typename === Typenames.CallUsPageSuccess
      ? data.callUsPage.options
      : [];

  const lastTileIndex = (callUsPageInfo.length ?? 0) - 1;

  return (
    <View style={styles.container}>
      <Stack.Screen
        options={{
          headerShown: true,
          headerBackVisible: true,
          title: '',
          header: (props: NativeStackHeaderProps) => (
            <ExpoNavigationBar {...props} />
          ),
        }}
      />
      <ScrollView
        refreshControl={
          <RefreshControl refreshing={fetching} onRefresh={refetch} />
        }
      >
        <View style={styles.titleContainer}>
          <TitleLgText numberOfLines={0}>
            {translatedText('callUs.contactSupport')}
          </TitleLgText>
        </View>
        <View style={styles.descriptionContainer}>
          <BodyBaseText>
            {translatedText('callUs.contactDescription')}
          </BodyBaseText>
        </View>
        {isWeb ? (
          <View style={styles.contactContainer}>
            <TitleMdText>{translatedText('callUs.contactNumber')}</TitleMdText>
          </View>
        ) : (
          callUsPageInfo.map((tile, index) => (
            <React.Fragment key={tile.id}>
              <ListItem
                primaryCopy={tile.title}
                // TODO JT-129338 - Add permanent solution for call us message
                onPress={() => openURL(Support.PHONE_NUMBER)}
                leadingAccessory={{
                  id: 'icon',
                  name: VXLComponentId.ICON,
                  props: {
                    iconKey: 'PHONE',
                  },
                }}
                analyticsId={
                  tile.title === 'File a dispute'
                    ? 'fileADispute'
                    : 'setupMobileWallet'
                }
                key={tile.id}
              />
              {!(index === lastTileIndex) ? (
                <View style={styles.hairlineDividerContainer}>
                  <HairLineDivider />
                </View>
              ) : null}
            </React.Fragment>
          ))
        )}
        {callUsPageInfo.length ? (
          <View style={styles.inlineAlertContainer}>
            <InlineAlert
              titleText={translatedText('hoursOfOperation')}
              bodyText={callUsPageInfo[0].message[0].replace(
                /(<([^>]+)>)/gi,
                '',
              )}
              alertType={InlineAlertType.Info}
              isImageVisible={false}
            />
          </View>
        ) : null}
      </ScrollView>
      <ScreenTransitionOverlay
        isLoading={fetching}
        LoadingComponent={<LoadingSpinner />}
        ErrorComponent={
          error ? (
            <DefaultPageDownErrorMessage
              error={error}
              title={defaultTitle}
              message={error.message || defaultMessage}
              tryAgainAction={refetch}
            />
          ) : null
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  contactContainer: {
    paddingBottom: theme.spacing.m,
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.s,
  },
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  descriptionContainer: {
    paddingBottom: !isWeb ? theme.spacing.s : 0,
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.s,
  },
  hairlineDividerContainer: {
    paddingHorizontal: theme.spacing.m,
  },
  inlineAlertContainer: {
    padding: theme.spacing.m,
  },
  titleContainer: {
    paddingHorizontal: theme.spacing.m,
    paddingTop: theme.spacing.m,
  },
});

export default CallUs;
